/**
 * After a deploy, a tab opened before it still runs the old version: the first screen it
 * loads on demand asks for a file of that version (`members-settings-<hash>.js`), which the
 * new one no longer has. Reloading fetches the new version. Done once: a second failure
 * within a short time is a real problem and shows the error screen instead of looping.
 */

const RELOADED_AT_KEY = 'crm:stale-chunk-reload-at';
/** A failure this soon after the last automatic reload is not the new version's fault. */
const LOOP_GUARD_MS = 30_000;

/** The messages browsers give when a module of the build cannot be fetched. */
const STALE_CHUNK_MESSAGES = [
  'Failed to fetch dynamically imported module', // Chrome, Edge
  'error loading dynamically imported module', // Firefox
  'Importing a module script failed', // Safari
  'Unable to preload CSS', // Vite's CSS preload
];

export function isStaleChunkError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  return STALE_CHUNK_MESSAGES.some((known) => message.includes(known));
}

/**
 * Reloads the page for the new version unless it just did; `true` when it reloads. The guard
 * lives in the tab's session storage, which may be unavailable (then it reloads anyway).
 */
export function reloadForNewVersion(
  reload: () => void = () => globalThis.location.reload(),
  now: number = Date.now(),
): boolean {
  try {
    const last = Number(globalThis.sessionStorage.getItem(RELOADED_AT_KEY) ?? 0);
    if (now - last < LOOP_GUARD_MS) return false;
    globalThis.sessionStorage.setItem(RELOADED_AT_KEY, String(now));
  } catch {
    // No storage (private mode, blocked): reload without the guard.
  }
  reload();
  return true;
}

/**
 * Vite fires `vite:preloadError` when a lazily loaded part of the build fails to load:
 * reload into the new version instead of letting the screen break.
 */
export function listenForStaleChunks(target: Window = window): void {
  target.addEventListener('vite:preloadError', (event) => {
    if (reloadForNewVersion()) event.preventDefault();
  });
}
